const CREATION_PROPS = new Set(['created', '__event']);
const SKIP_LEGEND = new Set(['create', 'delete', 'cancel']);

export const normalizeChanges = (changes) => {
  if (!changes) {
    return [];
  }
  if (typeof changes === 'string') {
    try {
      const parsed = JSON.parse(changes);
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  }
  return Array.isArray(changes) ? changes : [];
};

export const isCreationEntry = (entry) => {
  if (!entry || entry.legend === 'delete' || entry.legend === 'cancel') {
    return false;
  }
  if (entry.action === 'create' || entry.legend === 'create') {
    return true;
  }
  return normalizeChanges(entry.changes).some(change => change && CREATION_PROPS.has(change.prop));
};

export const getChangeVerb = (entry) => {
  if (entry && (entry.legend === 'delete' || entry.action === 'delete')) {
    return 'deleted';
  }
  if (isCreationEntry(entry)) {
    return 'created';
  }
  return 'changed';
};

export const visibleChanges = (entry) => (
  normalizeChanges(entry && entry.changes).filter(change => change && !CREATION_PROPS.has(change.prop))
);

const sameId = (left, right) => (
  left != null && right != null && String(left) === String(right)
);

export const isOrderEntry = entry => entry && !entry.placementId && !entry.packageId;

export const hasCreationForOrder = logs => (logs || []).some(entry => isCreationEntry(entry) && isOrderEntry(entry));

export const hasCreationForPackage = (logs, packageId) => (
  (logs || []).some(entry => isCreationEntry(entry) && sameId(entry.packageId, packageId))
);

export const hasCreationForPlacement = (logs, placementId) => (
  (logs || []).some(entry => isCreationEntry(entry) && sameId(entry.placementId, placementId))
);

const createdChange = () => ([{ prop: 'created', oldVal: null, newVal: true }]);

const creationEntry = (fields) => ({
  action: 'create',
  legend: 'create',
  userStr: null,
  changes: createdChange(),
  ...fields
});

const isRealPackage = (pkg) => pkg && pkg.id && pkg.name !== '{PLACEMENT}';

export const mergeCreationEntries = (changeLog, order) => {
  const base = (changeLog && !Array.isArray(changeLog))
    ? changeLog
    : { orderId: order && order.id, changes: [] };
  const existing = Array.isArray(base.changes) ? base.changes : [];
  if (!order) {
    return {
      ...base,
      changes: existing
    };
  }

  const additions = [];
  if (order.createdAt && !hasCreationForOrder(existing)) {
    additions.push(creationEntry({
      id: `created-order-${order.id}`,
      created: order.createdAt,
      agencyOrderName: order.agencyOrderName,
      agencyOrderId: order.agencyOrderId
    }));
  }

  (order.Packages || []).filter(isRealPackage).forEach((pkg) => {
    if (pkg.createdAt && !hasCreationForPackage(existing, pkg.id)) {
      additions.push(creationEntry({
        id: `created-package-${pkg.id}`,
        created: pkg.createdAt,
        packageId: pkg.id,
        ioId: order.id
      }));
    }
  });

  (order.Placements || []).forEach((placement) => {
    if (placement && placement.id && placement.createdAt && !hasCreationForPlacement(existing, placement.id)) {
      additions.push(creationEntry({
        id: `created-placement-${placement.id}`,
        created: placement.createdAt,
        placementId: placement.id,
        agencyPlacementName: placement.agencyPlacementName,
        agencyPlacementId: placement.agencyPlacementId,
        ioId: order.id
      }));
    }
  });

  const changes = existing.concat(additions).sort((left, right) => (
    new Date(right.created).getTime() - new Date(left.created).getTime()
  ));

  return {
    ...base,
    changes
  };
};

const legendSuffix = (entry) => {
  if (!entry || !entry.legend || SKIP_LEGEND.has(entry.legend)) {
    return '';
  }
  if (entry.placementId || entry.packageId) {
    return ` using ${entry.legend}`;
  }
  return '';
};

export const getChangeHeadline = (entry) => {
  const user = entry && entry.userStr ? entry.userStr.split(':')[0] : 'User';
  const verb = getChangeVerb(entry);
  if (entry && entry.placementId) {
    const name = entry.agencyPlacementName ? ` ${entry.agencyPlacementName}` : '';
    return `${user} ${verb} placement${name} [${entry.placementId}]${legendSuffix(entry)}`;
  }
  if (entry && entry.packageId) {
    return `${user} ${verb} package [${entry.packageId}]${legendSuffix(entry)}`;
  }
  return `${user} ${verb} order`;
};

export const getEntryKey = (entry, index) => {
  if (!entry) {
    return `changelog-${index}`;
  }
  if (entry.id != null) {
    return `${entry.placementId || entry.packageId || 'order'}-${entry.id}-${entry.created || index}`;
  }
  return `changelog-${index}`;
};
